/**
 * Serial number gate
 * ------------------
 * Blocks add-to-cart on gated products until the customer supplies a serial
 * number.
 *
 * Two ways in:
 *
 * 1. Automatic — any product form containing the hidden input rendered by
 *    snippets/serial-number-gate.liquid is intercepted, the modal opens, and
 *    the submit is handed back to the theme once a serial is entered.
 *
 * 2. Manual — for custom add-to-cart code (addons, upsells, bundles) that does
 *    not go through a product form:
 *
 *      const serial = await window.SerialGate.prompt();
 *      if (!serial) return;                       // customer cancelled
 *      properties[window.SerialGate.PROPERTY] = serial;
 *
 *    Calls are queued, so prompting for several gated items at once is safe.
 */
(function () {
  'use strict';

  /* ---------------------------------------------------------------- copy */

  var CONFIG = {
    heading: 'Confirm your Instacheck model',
    body: 'Enter your Instacheck serial number so we can ship the correct battery.',
    findTitle: 'Where to find your serial number',
    imageText: 'The serial number is printed on the bottom of your Instacheck device.',
    label: 'Serial number',
    placeholder: 'xxxxxxxxxxxxxx',
    helpText: 'Need help finding your serial number?',
    helpUrl: '', // e.g. '/pages/find-your-serial-number' — leave blank to hide the link
    submitLabel: 'Add to cart',
    cancelLabel: 'Cancel',

    // Minimum length, counted after whitespace is stripped. Set to 0 to disable.
    minLength: 14,

    // Leave as null to accept any non-empty value of the right length.
    // For exactly 14 numeric digits instead, use: /^\d{14}$/
    pattern: null,

    requiredError: 'Enter your serial number to continue.',
    lengthError:
      'Serial numbers are at least 14 characters. Check the full number on the label.',
    patternError:
      'That does not look like a valid serial number. Check the label on the bottom of your device.'
  };

  /** Line item property name. Must match the snippet's input name. */
  var PROPERTY = 'Serial Number';

  /* --------------------------------------------------------------- state */

  var INPUT_SELECTOR = 'input[data-serial-gate]';
  var BYPASS_ATTR = 'data-serial-ok';
  var BYPASS_WINDOW = 1000; // ms a confirmed submit stays valid

  var dialog = null;
  var el = {};
  var session = null; // { settle, restoreFocus }
  var queue = Promise.resolve();

  // Set window.SERIAL_GATE_DEBUG = true in the console to trace the flow.
  function debug() {
    if (!window.SERIAL_GATE_DEBUG) return;
    console.log.apply(console, ['[serial-gate]'].concat(Array.prototype.slice.call(arguments)));
  }

  /* --------------------------------------------------------------- utils */

  function isAddToCartForm(form) {
    return (
      form instanceof HTMLFormElement &&
      (form.getAttribute('action') || '').indexOf('/cart/add') !== -1
    );
  }

  /** Customers paste serials with stray spaces — strip them before checking. */
  function normalize(value) {
    return String(value == null ? '' : value).replace(/\s+/g, '');
  }

  function validate(value) {
    if (!value) return CONFIG.requiredError;
    if (CONFIG.minLength && value.length < CONFIG.minLength) return CONFIG.lengthError;
    if (CONFIG.pattern && !CONFIG.pattern.test(value)) return CONFIG.patternError;
    return null;
  }

  /**
   * Best guess at the button the customer pressed, for when event.submitter is
   * null. Impact's product card renders two submit buttons — an icon for touch
   * and a labelled one for pointer devices — and only one of them is visible,
   * so prefer a rendered button over the first in the DOM.
   */
  function findSubmitter(form) {
    var buttons = Array.prototype.slice.call(
      form.querySelectorAll('[name="add"], button[type="submit"], [type="submit"]')
    );

    var visible = buttons.filter(function (button) {
      return button.offsetParent !== null || button.getClientRects().length > 0;
    });

    return visible[0] || buttons[0] || null;
  }

  /** Tag gated forms so CSS can hide express checkout buttons. */
  function markForms(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var inputs = Array.prototype.slice.call(scope.querySelectorAll(INPUT_SELECTOR));

    if (scope.matches && scope.matches(INPUT_SELECTOR)) inputs.push(scope);

    inputs.forEach(function (input) {
      var form = input.form || input.closest('form');
      if (form) form.setAttribute('data-serial-gated', '');
    });
  }

  /* -------------------------------------------------------------- dialog */

  function buildDialog() {
    if (dialog) return dialog;

    dialog = document.createElement('dialog');
    dialog.className = 'serial-gate';
    dialog.setAttribute('aria-labelledby', 'serial-gate-heading');
    dialog.innerHTML = [
      '<div class="serial-gate__panel">',
      '  <button type="button" class="serial-gate__close" data-sg="close" aria-label="Close">',
      '    <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" focusable="false">',
      '      <path d="M1 1l14 14M15 1L1 15" stroke="currentColor" stroke-width="1.5" fill="none"/>',
      '    </svg>',
      '  </button>',
      '  <h2 class="serial-gate__heading" id="serial-gate-heading" data-sg="heading"></h2>',
      '  <p class="serial-gate__body" data-sg="body"></p>',
      '  <div class="serial-gate__find" data-sg="find">',
      '    <p class="serial-gate__find-title" data-sg="find-title"></p>',
      '    <div class="serial-gate__media">',
      '      <img class="serial-gate__image" data-sg="image" alt="" loading="lazy" hidden>',
      '      <p class="serial-gate__image-text" data-sg="image-text"></p>',
      '    </div>',
      '  </div>',
      '  <label class="serial-gate__label" for="serial-gate-input" data-sg="label"></label>',
      '  <input class="serial-gate__input" id="serial-gate-input" data-sg="input" type="text"',
      '    autocomplete="off" autocapitalize="characters" spellcheck="false"',
      '    aria-describedby="serial-gate-error">',
      '  <p class="serial-gate__error" id="serial-gate-error" data-sg="error" role="alert" hidden></p>',
      '  <a class="serial-gate__help" data-sg="help" target="_blank" rel="noopener" hidden></a>',
      '  <div class="serial-gate__actions">',
      '    <button type="button" class="serial-gate__button serial-gate__button--ghost" data-sg="cancel"></button>',
      '    <button type="button" class="serial-gate__button serial-gate__button--primary" data-sg="confirm"></button>',
      '  </div>',
      '</div>'
    ].join('');

    document.body.appendChild(dialog);

    dialog.querySelectorAll('[data-sg]').forEach(function (node) {
      el[node.getAttribute('data-sg')] = node;
    });

    el.body.textContent = CONFIG.body;
    el.body.hidden = !CONFIG.body;
    el['find-title'].textContent = CONFIG.findTitle;
    el['find-title'].hidden = !CONFIG.findTitle;
    el['image-text'].textContent = CONFIG.imageText;
    el['image-text'].hidden = !CONFIG.imageText;
    el.label.textContent = CONFIG.label;
    el.input.placeholder = CONFIG.placeholder;
    el.confirm.textContent = CONFIG.submitLabel;
    el.cancel.textContent = CONFIG.cancelLabel;

    if (CONFIG.helpUrl && CONFIG.helpText) {
      el.help.textContent = CONFIG.helpText;
      el.help.href = CONFIG.helpUrl;
      el.help.hidden = false;
    }

    // A missing image file should not leave a broken icon in the modal.
    el.image.addEventListener('error', function () {
      el.image.hidden = true;
    });

    el.confirm.addEventListener('click', confirm);
    el.cancel.addEventListener('click', close);
    el.close.addEventListener('click', close);

    el.input.addEventListener('input', function () {
      el.error.hidden = true;
      el.input.removeAttribute('aria-invalid');
    });

    el.input.addEventListener('keydown', function (event) {
      if (event.key === 'Enter') {
        event.preventDefault();
        confirm();
      }
    });

    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) close();
    });

    // Covers Esc, backdrop clicks and Cancel alike.
    dialog.addEventListener('close', function () {
      settle(null);
    });

    return dialog;
  }

  function settle(value) {
    if (!session) return;

    var current = session;
    session = null;

    if (current.restoreFocus && document.contains(current.restoreFocus)) {
      current.restoreFocus.focus();
    }

    current.settle(value);
  }

  function close() {
    if (!dialog) return;

    if (typeof dialog.close === 'function' && dialog.open) {
      dialog.close(); // fires 'close', which settles with null
    } else {
      dialog.removeAttribute('open');
      settle(null);
    }
  }

  function confirm() {
    if (!session) return;

    var value = normalize(el.input.value.trim());
    var message = validate(value);

    if (message) {
      el.error.textContent = message;
      el.error.hidden = false;
      el.input.setAttribute('aria-invalid', 'true');
      el.input.focus();
      return;
    }

    debug('confirmed', value);

    var current = session;
    session = null;

    if (typeof dialog.close === 'function' && dialog.open) {
      // Null out the session first so the 'close' handler cannot overwrite the
      // resolved value with a cancellation.
      dialog.close();
    } else {
      dialog.removeAttribute('open');
    }

    if (current.restoreFocus && document.contains(current.restoreFocus)) {
      current.restoreFocus.focus();
    }

    current.settle(value);
  }

  /**
   * Open the modal. Resolves with the serial number, or null if the customer
   * backed out.
   *
   * @param {Object} [options]
   * @param {string} [options.image]   URL of the "where to find it" diagram
   * @param {string} [options.value]   prefilled value
   * @param {string} [options.heading] override the modal title
   */
  function openModal(options) {
    var opts = options || {};

    return new Promise(function (resolve) {
      buildDialog();

      if (dialog.open) {
        debug('modal already open — refusing to stack');
        resolve(null);
        return;
      }

      session = { settle: resolve, restoreFocus: document.activeElement };

      el.heading.textContent = opts.heading || CONFIG.heading;

      var imageUrl = opts.image || '';
      if (imageUrl) {
        if (el.image.getAttribute('src') !== imageUrl) el.image.src = imageUrl;
        el.image.hidden = false;
      } else {
        el.image.hidden = true;
      }
      el.find.hidden = !CONFIG.findTitle && !CONFIG.imageText && el.image.hidden;

      el.input.value = opts.value || '';
      el.input.removeAttribute('aria-invalid');
      el.error.hidden = true;

      if (typeof dialog.showModal === 'function') {
        dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
        dialog.classList.add('serial-gate--fallback');
      }

      window.setTimeout(function () {
        el.input.focus();
        el.input.select();
      }, 50);
    });
  }

  /** Queued so several gated items in one action prompt one after another. */
  function prompt(options) {
    var run = function () {
      return openModal(options);
    };

    var result = queue.then(run, run);

    queue = result.catch(function () {});

    return result;
  }

  /* ----------------------------------------------------------- form path */

  /**
   * Hand the submit back to the theme.
   *
   * The bypass is a timestamp rather than a one-shot flag: Impact's card
   * buttons can produce more than one submit for a single click, and every
   * one of them has to pass, not just the first.
   */
  function submitForm(form, submitter) {
    form.setAttribute(BYPASS_ATTR, String(Date.now()));

    window.setTimeout(function () {
      if (submitter && submitter.form === form && typeof submitter.click === 'function') {
        debug('re-submitting via click on', submitter, 'disabled:', submitter.disabled);
        submitter.click();
      } else if (typeof form.requestSubmit === 'function') {
        debug('re-submitting via requestSubmit');
        form.requestSubmit();
      } else {
        form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      }

      window.setTimeout(function () {
        form.removeAttribute(BYPASS_ATTR);
      }, BYPASS_WINDOW);
    }, 0);
  }

  // Capture phase on document runs before any listener the theme binds to the
  // form itself, so the theme never sees a submit without a serial number.
  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!isAddToCartForm(form)) return;

      var input = form.querySelector(INPUT_SELECTOR);
      if (!input) return;

      var okAt = parseInt(form.getAttribute(BYPASS_ATTR) || '0', 10);
      debug('submit intercepted', form.id, 'flag age:', okAt ? Date.now() - okAt : 'none');

      if (okAt && Date.now() - okAt < BYPASS_WINDOW) {
        debug('passing through to theme');
        return;
      }

      form.removeAttribute(BYPASS_ATTR);

      event.preventDefault();
      event.stopImmediatePropagation();

      var submitter = event.submitter || findSubmitter(form);

      prompt({ image: input.getAttribute('data-serial-image'), value: input.value }).then(
        function (serial) {
          if (!serial) return; // cancelled
          input.value = serial;
          submitForm(form, submitter);
        }
      );
    },
    true
  );

  /* ---------------------------------------------------------------- init */

  window.SerialGate = {
    prompt: prompt,
    validate: validate,
    normalize: normalize,
    PROPERTY: PROPERTY,
    CONFIG: CONFIG
  };

  function init() {
    markForms(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Sections re-render on variant change / quick buy — re-tag the new markup.
  new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      mutation.addedNodes.forEach(function (node) {
        if (node.nodeType === 1) markForms(node);
      });
    });
  }).observe(document.documentElement, { childList: true, subtree: true });
})();